import React, { FC, ReactNode, useEffect, useState } from 'react';
import { Button, Table } from 'antd';
import { InfiniteScroll, Spin, WarningCard } from 'common/components';
import { showMessage, showSuccessMessage } from 'common/helpers';
import { LIST_LIMIT_0 } from 'common/config';
import { Bin, Plus } from 'common/svg';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { AddressCreateCard } from 'entities/Address/components';
import { IAddress, ICreateAddressPayload } from 'entities/Address/Address.models';
import { renderSettingsAccountAddressesRecord } from 'entities/Settings/Settings.helper';
import { IAccount } from 'entities/Account/Account.models';
import {
  createAddress,
  deleteAddress,
  getAddressList,
  selectAddressList,
  selectIsAddressLoading,
  setAddressList,
  updateAddress,
} from 'entities/Address/Address.slice';

interface IComponentProps {
  header: ReactNode;
  account: IAccount | null;
}

export const SettingsAccountAddressesTab: FC<IComponentProps> = ({ header, account }) => {
  const dispatch = useAppDispatch();
  // address
  const addressList = useAppSelector(selectAddressList);
  const isAddressLoading = useAppSelector(selectIsAddressLoading);

  const [fetchLoading, setFetchLoading] = useState<boolean>(true);
  const [openWarningCard, setOpenWarningCard] = useState<boolean>(false);
  const [openAddressCreateCard, setOpenAddressCreateCard] = useState<boolean>(false);
  const [address, setAddress] = useState<IAddress | null>(null);

  useEffect(() => {
    const fetch = async () => {
      setFetchLoading(true);

      if (account?.id) {
        dispatch(setAddressList([]));
        await dispatch(
          getAddressList({
            accountId: account.id,
            limit: LIST_LIMIT_0,
          }),
        );
      }

      setFetchLoading(false);
    };

    fetch();
  }, [account?.id]);

  const showAddressCreateCard = (record?: IAddress) => {
    setAddress(record ? record : null);
    setOpenAddressCreateCard(true);
  };

  const closeAddressCreateCard = () => {
    setOpenAddressCreateCard(false);
  };

  const handleCreateAddress = ({ onSuccess, ...rest }: ICreateAddressPayload) => {
    dispatch(
      createAddress({
        ...rest,
        onSuccess: () => {
          showSuccessMessage('Новый адрес добавлен в аккаунт.');
          onSuccess?.();
        },
      }),
    );
  };

  const handleUpdateAddress = ({ onSuccess, ...rest }: ICreateAddressPayload) => {
    if (!address) {
      return;
    }

    dispatch(
      updateAddress({
        ...rest,
        id: address.id,
        onSuccess: () => {
          showSuccessMessage('Адрес доставки успешно изменен.');
          onSuccess?.();
        },
      }),
    );
  };

  const openAddressDeleteCard = (record: IAddress) => {
    setAddress(record);
    setOpenWarningCard(true);
  };

  const closeDeleteCard = () => {
    setOpenWarningCard(false);
  };

  const handleDeleteAddress = () => {
    if (!address) {
      return;
    }

    dispatch(
      deleteAddress({
        id: address.id,
        onSuccess: () => {
          showMessage({
            content: `Адрес доставки «${address.postalCode}, ${address.city.name}, ${address.name}» удален.`,
            icon: <Bin className="icon-bin-dark-grey" />,
          });
          setOpenWarningCard(false);
        },
      }),
    );
  };

  return (
    <>
      <InfiniteScroll>
        {header}

        <div className="mb-6">
          <Button
            className="button-sm primary"
            icon={<Plus className="icon-plus-white" />}
            onClick={() => showAddressCreateCard()}
          >
            Добавить адрес
          </Button>
        </div>

        <Table
          className={`${!addressList.length && 'table-empty'} mb-19 subdivision-address-list-settings__table`}
          showHeader={false}
          pagination={false}
          size="middle"
          dataSource={addressList.map((address: any) => ({ ...address, key: address.id }))}
          columns={renderSettingsAccountAddressesRecord(showAddressCreateCard, openAddressDeleteCard)}
          locale={{
            emptyText: fetchLoading ? <Spin /> : <div className="text-body color-dark-grey">В аккаунте ещё нет адресов.</div>,
          }}
        />
      </InfiniteScroll>

      <AddressCreateCard
        open={openAddressCreateCard}
        loading={isAddressLoading}
        address={address}
        onCancel={closeAddressCreateCard}
        onSubmit={address ? handleUpdateAddress : handleCreateAddress}
      />

      <WarningCard
        open={openWarningCard}
        content="Вы уверены, что хотите удалить адрес доставки?"
        subtitle="Отменить данное действие будет невозможно."
        confirmBtnTitle="Удалить"
        loading={isAddressLoading}
        onConfirm={handleDeleteAddress}
        onClose={closeDeleteCard}
      />
    </>
  );
};
